import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useLoginMutation } from "../store/api/adminAuthApi";
import { setCredentials } from "../store/authSlice";
import { useToast } from "../components/Toast";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");

  const [login, { isLoading }] = useLoginMutation();

  useEffect(() => {
    if (sessionStorage.getItem("session_expired")) {
      sessionStorage.removeItem("session_expired");
      toast.error("Your session has expired. Please sign in again.");
    }
  }, [toast]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const result = await login({ email, password }).unwrap();
      dispatch(setCredentials({ token: result.data.token, user: result.data.user }));
      navigate("/");
    } catch (err) {
      setError(err?.data?.error?.message || "Login failed. Please check your credentials.");
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex flex-col items-center leading-tight mb-3">
            <span className="font-manrope-bold text-[22px] text-gray-900 tracking-[0.2em] uppercase">
              CONTENTOUR BONAIRE
            </span>
            <span className="font-manrope-regular text-[13px] text-gray-400 tracking-[0.35em] uppercase">
              Admin
            </span>
            <div className="flex gap-[3px] mt-1.5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-[8px] h-[3px] bg-primary rounded-full" />
              ))}
            </div>
          </div>
          <p className="text-[13px] font-manrope-regular text-gray-400 mt-2">
            Sign in to your admin account
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm p-8">
          <h1 className="text-[20px] font-manrope-bold text-gray-900 mb-1">
            Welcome back
          </h1>
          <p className="text-[12px] font-manrope-regular text-gray-400 mb-6">
            Enter your credentials to access the dashboard.
          </p>

          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            {/* Email */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <Mail size={15} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock size={15} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPass ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all"
                >
                  {showPass ? <EyeOff size={15} strokeWidth={1.6} /> : <Eye size={15} strokeWidth={1.6} />}
                </button>
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => navigate("/recover-password")}
                className="text-[12px] font-manrope-semibold text-primary hover:opacity-75 transition-all"
              >
                Forgot password?
              </button>
            </div>

            {/* Error */}
            {error && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl -mt-1">
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl text-white text-[14px] font-manrope-bold hover:opacity-90 disabled:opacity-60 transition-all mt-1 bg-primary"
            >
              {isLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] font-manrope-regular text-gray-400 mt-6">
          © {new Date().getFullYear()} Contentour Bonaire. All rights reserved.
        </p>
      </div>
    </div>
  );
}
